import React from 'react';
import {Breakpoints, useIsMobile} from '@vw-marketing/us-components';
import Slider, {LazyLoadTypes} from 'react-slick';
import {StyledSimilarVehiclesSlider, StyledSlickContainer} from './styles';
import {UsedVehicle} from '../../typing/used-inventory';
import SearchResultsVehicleCard from '../search-results-vehicle-card';
import ArrowRight from '../icons/arrow-right';
import ArrowLeft from '../icons/arrow-left';
import {useHistory} from '../../hooks/use-history';
import {hasBeforeResults, modalsData} from '../../store/local-storage';
import {onVWBasicVehicleButtonClick} from '../../tagging/tagging-helpers';
import {ModalDisplayMode} from '../../typing/modal-display';
import {useTrackingManager} from '../../hooks/use-tracking-manager';

interface SimilarVehiclesSliderProps {
  readonly vehicles: UsedVehicle[];
  readonly priceDisclaimer?: string;
  readonly getWarrantyDisclaimer?: (year?: string) => string | undefined;
}

export const SimilarVehiclesSlider = ({
  vehicles,
  priceDisclaimer,
  getWarrantyDisclaimer,
}: SimilarVehiclesSliderProps): JSX.Element => {
  const trackingManager = useTrackingManager();
  const isMobile = useIsMobile(Breakpoints.b960);
  const history = useHistory();

  const onHandleRedirectVehicle = (vehicle: UsedVehicle) => {
    const href = `/cpo/vehicle/${vehicle.vin}`;
    history.push(href);
    hasBeforeResults(false);
  };

  const arrowContent = (label: string, direction: 'left' | 'right') => (
    <div>
      <span className={`arrow-container-${direction}`}>
        {direction === 'left' ? (
          <ArrowLeft iconSize={24} />
        ) : (
          <ArrowRight iconSize={24} />
        )}
        <span>{label}</span>
      </span>
    </div>
  );

  const onHandleBuyingTransition = (
    vehicle: UsedVehicle,
    vehiclePosition: number,
  ) => {
    const filter = {url: window.location.href, name: 'Build my Deal'};
    onVWBasicVehicleButtonClick(vehicle, filter, trackingManager);
    modalsData({
      vehicle,
      active: true,
      mode: ModalDisplayMode.HANDOFF_BUYING,
      vehiclePosition,
    });
  };

  let slidesToShow = isMobile ? 1 : 3.2;

  const sliderSettings = {
    dots: false,
    arrows: true,
    infinite: false,
    swipe: isMobile,
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    focusOnSelect: true,
    prevArrow: arrowContent('Previous', 'left'),
    nextArrow: arrowContent('Next', 'right'),
    lazyLoad: 'ondemand' as LazyLoadTypes,
  };

  return (
    <StyledSimilarVehiclesSlider className="similar-vehicles-slider">
      {!!vehicles.length && (
        <>
          <StyledSlickContainer className="similar-vehicles-slider-container">
            <Slider {...sliderSettings}>
              {vehicles.map((vehicle: UsedVehicle, index: number) => {
                return (
                  <SearchResultsVehicleCard
                    key={vehicle.vin}
                    vehicle={vehicle}
                    onHandleVehicleClick={onHandleRedirectVehicle.bind(
                      null,
                      vehicle,
                    )}
                    priceDisclaimer={priceDisclaimer}
                    warrantyDisclaimer={
                      getWarrantyDisclaimer &&
                      getWarrantyDisclaimer(vehicle?.modelYear)
                    }
                    startBuyingHandler={onHandleBuyingTransition.bind(
                      null,
                      vehicle,
                      index + 1,
                    )}
                  />
                );
              })}
            </Slider>
          </StyledSlickContainer>
        </>
      )}
    </StyledSimilarVehiclesSlider>
  );
};
